import { LitElement, html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ref } from 'lit/directives/ref.js';
import icons from '../foundation/icons.js';
import style from '../../../public/assets/css/vi-dialog.css';

const translations = {
	en: {
		close: 'Close modal',
	},
	de: {
		close: 'Dialog schließen',
	},
	es: {
		close: 'Cerrar modal',
	},
	fr: {
		close: 'Fermer le modal',
	},
};

class ViDialog extends LitElement {
	static styles = [style];

	static properties = {
		open: { type: Boolean },
	};

	dialogElement;

	/** Button element which opened the modal */
	targetButton = null;

	get translations() {
		const language = this.closest('[lang]')?.lang ?? document.documentElement.lang;
		return translations[language] ?? translations.en;
	}

	get backdropElement() {
		return this.renderRoot.querySelector('.backdrop');
	}

	get contentElement() {
		return this.renderRoot.querySelector('.content');
	}

	#onBackdropClick(event) {
		if (event.target === this.backdropElement) {
			this.dialogElement.dispatchEvent(new Event('close'));
		}
	}

	#onButtonCloseClick() {
		this.dialogElement.dispatchEvent(new Event('close'));
	}

	get showFooter() {
		const assignedElementsCount = [...this.shadowRoot.querySelectorAll('.footer slot')]
			.reduce((accumulator, _) => accumulator + _.assignedElements().length, 0);
		return assignedElementsCount !== 0;
	}

	showModal() {
		if (!this.hasAttribute('open')) {
			const animationAllowed = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
			this.targetButton = document.activeElement.tagName.toLowerCase() === 'button' ? document.activeElement : null;
			this.toggleAttribute('open', true);
			this.htmlElement.style.overflow = 'hidden';
			this.dialogElement.showModal();
			this.dialogElement.focus();
			this.backdropElement.animate({
				opacity: [0, 1],
			}, {
				duration: 250,
				easing: 'ease-in',
			});
			this.contentElement.animate({
				translate: ['0 1rem', '0 0'],
				opacity: [0, 1],
			}, {
				duration: animationAllowed ? 200 : 0,
				delay: animationAllowed ? 50 : 0,
				fill: 'backwards',
				easing: 'cubic-bezier(0.215, 0.610, 0.6, 1.6)',
			});
		}
	}

	close() {
		if (this.hasAttribute('open')) {
			const animationAllowed = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

			this.contentElement.animate({
				translate: ['0 0', '0 1rem'],
				opacity: ['1', '0'],
			}, {
				duration: animationAllowed ? 200 : 0,
				delay: animationAllowed ? 20 : 0,
				easing: 'linear',
			});
			const animation = this.backdropElement.animate({
				opacity: 0,
			}, {
				duration: 200,
				easing: 'linear',
			});

			animation.addEventListener('finish', () => {
				this.toggleAttribute('open', false);
				this.htmlElement.style.overflow = null;
				this.dialogElement.close();

				this.dispatchEvent(new CustomEvent('close'));

				this.targetButton?.focus();
			});
		}
	}

	onClose(event) {
		// Close event of this.dialogElement
		event.preventDefault();
		this.close();
	}

	connectedCallback() {
		super.connectedCallback();

		this.htmlElement = document.querySelector('html');

		this.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				this.close();
			}
		});

		this.addEventListenersForButtonElements();

		this.dispatchEvent(new CustomEvent('connected'));
	}

	addEventListenersForButtonElements() {
		document
			.querySelectorAll(`button[data-command="show-modal"][data-commandfor="${this.id}"]`)
			.forEach((_) => {
				_.addEventListener('click', this.showModal.bind(this));
			});
	}

	render() {
		const { showFooter } = this;

		return html`
			<dialog ${ref((_) => { this.dialogElement = _; })} tabindex="0" @close=${this.onClose}>
				<div class="backdrop" @click=${this.#onBackdropClick}>
					<div class="content">
						<div class="header">
							<slot name="header"></slot>
							<button
								class="a-button"
								data-shape="round"
								data-kind="tertiary"
								data-size="large"
								aria-label="${this.translations.close}"
								@click=${this.#onButtonCloseClick}
							>${unsafeHTML(icons.close)}</button>
						</div>
						<slot></slot>
						<div class="footer" .hidden="${!showFooter ?? nothing}">
							<slot name="secondary-buttons"></slot>
							<slot name="primary-button"></slot>
						</div>
					</div>
				</div>
			</dialog>
		`;
	}
}

customElements.define('vi-dialog', ViDialog);
